import { useCallback, useEffect, useRef, useState } from 'react';
import { useOkapiKy } from '@folio/stripes/core';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS  = 10 * 60 * 1000; // 10 minutes

export const EXEC_STAGE = {
  IDLE    : 'idle',
  RUNNING : 'running',
  SUCCESS : 'success',
  FAILED  : 'failed',
  TIMEOUT : 'timeout',
};

export default function useLastExecution(scheduleId) {
  const okapiKy    = useOkapiKy();
  // Store okapiKy in a ref so it never causes useEffect to re-run
  const okapiKyRef = useRef(okapiKy);
  useEffect(() => { okapiKyRef.current = okapiKy; }, [okapiKy]);

  const pollRef  = useRef(null);
  const startRef = useRef(null);

  const [stage,     setStage]     = useState(EXEC_STAGE.IDLE);
  const [execution, setExecution] = useState(null);
  const [elapsed,   setElapsed]   = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // One-shot fetch — stable, only depends on scheduleId
  const fetchLastExecution = useCallback(async () => {
    if (!scheduleId) return;
    try {
      const res = await okapiKyRef.current
        .get(`report-executions?query=scheduledReportId%3D%3D${scheduleId}%20sortby%20executionDate%2Fsort.descending&limit=1`)
        .json();
      const records = res?.reportExecutions || [];
      if (records.length) {
        const latest = records[0];
        setExecution(latest);
        setStage(latest.status === 'success' ? EXEC_STAGE.SUCCESS
          : latest.status === 'failed'  ? EXEC_STAGE.FAILED
          : EXEC_STAGE.RUNNING);
      } else {
        setStage(EXEC_STAGE.IDLE);
      }
    } catch (_) {
      // silent
    }
  }, [scheduleId]); // only scheduleId — okapiKy via ref

  // Active polling — called right after run-now
  const startPolling = useCallback(() => {
    stopPolling();
    setStage(EXEC_STAGE.RUNNING);
    setExecution(null);
    setElapsed(0);
    setIsPolling(true);
    startRef.current = Date.now();

    pollRef.current = setInterval(async () => {
      const now = Date.now();
      setElapsed(Math.floor((now - startRef.current) / 1000));

      if (now - startRef.current > POLL_TIMEOUT_MS) {
        stopPolling();
        setStage(EXEC_STAGE.TIMEOUT);
        return;
      }

      try {
        const res = await okapiKyRef.current
          .get(`report-executions?query=scheduledReportId%3D%3D${scheduleId}%20sortby%20executionDate%2Fsort.descending&limit=1`)
          .json();
        const records = res?.reportExecutions || [];
        if (!records.length) return;

        const latest = records[0];
        if (latest.status === 'success') {
          stopPolling();
          setExecution(latest);
          setStage(EXEC_STAGE.SUCCESS);
        } else if (latest.status === 'failed') {
          stopPolling();
          setExecution(latest);
          setStage(EXEC_STAGE.FAILED);
        }
      } catch (_) { /* keep polling */ }
    }, POLL_INTERVAL_MS);
  }, [scheduleId, stopPolling]); // okapiKy via ref — no infinite loop

  // Fetch once on mount — stable deps, no infinite loop
  useEffect(() => {
    fetchLastExecution();
    return stopPolling;
  }, [scheduleId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only re-run when scheduleId changes, not on every okapiKy reference change

  return {
    stage,
    execution,
    elapsed,
    isPolling,
    startPolling,
    stopPolling,
    fetchLastExecution,
  };
}
